import React, { useState, useEffect } from 'react';
import { 
  Bot, 
  FolderOpen, 
  FileText, 
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Users,
  Play,
  Square,
  Activity
} from 'lucide-react';
import BotControl from '../components/BotControl';
import StatsCard from '../components/StatsCard';
import { analyticsAPI, botAPI } from '../services/api';
import { sessionsAPI } from '../services/sessionsAPI';

function Dashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [botStatus, setBotStatus] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 10000); // Poll every 10 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      console.log('Fetching dashboard data...');
      const [analyticsResponse, statusResponse, sessionsResponse] = await Promise.all([
        analyticsAPI.getOverview(),
        botAPI.getStatus(),
        sessionsAPI.getAll()
      ]);
      
      console.log('Analytics response:', analyticsResponse.data);
      console.log('Status response:', statusResponse.data);
      console.log('Sessions response:', sessionsResponse.data);
      
      setAnalytics(analyticsResponse.data);
      setBotStatus(statusResponse.data);
      setSessions(sessionsResponse.data.sessions || []);
      setError(null);
    } catch (err) {
      setError('Failed to fetch dashboard data');
      console.error('Error fetching dashboard data:', err);
      console.error('Error details:', err.response?.data);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSessionAction = async (sessionId, action) => {
    try {
      if (action === 'start') {
        await sessionsAPI.startBot(sessionId);
      } else if (action === 'stop') {
        await sessionsAPI.stopBot(sessionId);
      }
      // Refresh data after action
      fetchDashboardData();
    } catch (err) {
      console.error(`Failed to ${action} session:`, err);
      setError(`Failed to ${action} session`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <XCircle className="mx-auto h-12 w-12 text-danger-400" />
        <h3 className="mt-2 text-sm font-medium text-slate-100">Error</h3>
        <p className="mt-1 text-sm text-slate-500">{error}</p>
        <button
          onClick={fetchDashboardData}
          className="mt-4 btn btn-primary"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-100">Dashboard</h1>
        <div className="text-sm text-slate-500">
          Last updated: {new Date().toLocaleTimeString()}
        </div>
      </div>

      {/* Bot Control Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* <div className="lg:col-span-2">
          <BotControl />
        </div> */}
        {/* <div className="space-y-4"> */}
          <div className="card">
            <h3 className="text-lg font-semibold text-slate-100 mb-4">Quick Stats</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Active Sessions</span>
                <span className="font-medium text-success-600">{analytics?.running_sessions || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Total Sessions</span>
                <span className="font-medium">{analytics?.total_sessions || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Total Bids Today</span>
                <span className="font-medium">{analytics?.total_bids || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-400">Success Rate</span>
                <span className="font-medium">{analytics?.success_rate || 0}%</span>
              </div>
            </div>
          </div>
          
          {/* Session Performance Chart Placeholder */}
          <div className="card">
            <h3 className="text-lg font-semibold text-slate-100 mb-4">Session Performance</h3>
            <div className="space-y-2">
              {analytics?.sessions?.slice(0, 999).map((session, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className={`w-2 h-2 rounded-full ${
                      session.is_running ? 'bg-success-500' : 'bg-slate-500'
                    }`} />
                    <span className="text-sm text-slate-400">
                      {session.name || `Session ${index + 1}`}
                    </span>
                  </div>
                  <div className="text-sm font-medium">
                    {session.total_bids || 0} bids
                  </div>
                </div>
              )) || (
                <div className="text-center py-4 text-slate-500 text-sm">
                  No session data available
                </div>
              )}
            </div>
          </div>
        {/* </div> */}
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Projects"
          value={analytics?.total_projects || 0}
          icon={FolderOpen}
          color="primary"
        />
        <StatsCard
          title="Total Bids"
          value={analytics?.total_bids || 0}
          icon={FileText}
          color="success"
        />
        <StatsCard
          title="Success Rate"
          value={`${analytics?.success_rate || 0}%`}
          icon={TrendingUp}
          color="warning"
        />
        <StatsCard
          title="Active Sessions"
          value={analytics?.running_sessions || 0}
          icon={Bot}
          color="danger"
        />
      </div>

      {/* Session Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-slate-100 mb-4">Session Overview</h3>
          <div className="space-y-3">
            {analytics?.sessions?.slice(0, 5).map((session, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-slate-800/60 border border-slate-700/50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`w-2 h-2 rounded-full ${
                    session.is_running ? 'bg-success-500' : 
                    session.status === 'stopped' ? 'bg-slate-500' : 'bg-danger-500'
                  }`} />
                  <div>
                    <div className="text-sm font-medium text-slate-100">
                      {session.name || `Session ${session.session_id?.slice(0, 8)}...`}
                    </div>
                    <div className="text-xs text-slate-500">
                      {session.start_time ? new Date(session.start_time).toLocaleString() : 'Not running'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="text-right">
                    <div className="text-sm font-medium text-slate-100">
                      {session.bid_counter || 0}/{session.bid_limit || 0} bids
                    </div>
                    <div className={`text-xs ${
                      session.is_running ? 'text-success-600' : 
                      session.status === 'stopped' ? 'text-slate-500' : 'text-danger-600'
                    }`}>
                      {session.is_running ? 'Running' : 'Stopped'}
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    {session.is_running ? (
                      <button
                        onClick={() => handleSessionAction(session.session_id, 'stop')}
                        className="p-1 text-danger-600 hover:bg-danger-950/40 rounded"
                        title="Stop Session"
                      >
                        <Square className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSessionAction(session.session_id, 'start')}
                        className="p-1 text-success-600 hover:bg-success-950/40 rounded"
                        title="Start Session"
                      >
                        <Play className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )) || (
              <div className="text-center py-4 text-slate-500">
                No sessions found
              </div>
            )}
          </div>
        </div>

        <div className="card">
          <h3 className="text-lg font-semibold text-slate-100 mb-4">Session Statistics</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Total Sessions</span>
              <span className="font-medium">{analytics?.total_sessions || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Running Sessions</span>
              <span className="font-medium text-success-600">{analytics?.running_sessions || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Total Bids Today</span>
              <span className="font-medium">{analytics?.total_bids || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-400">Average Success Rate</span>
              <span className="font-medium">{analytics?.success_rate || 0}%</span>
            </div>
          </div>
          
          {/* Quick Session Actions */}
          <div className="mt-4 pt-4 border-t border-slate-700">
            <h4 className="text-sm font-medium text-slate-100 mb-2">Quick Actions</h4>
            <div className="flex space-x-2">
              <a href="/sessions" className="flex-1">
              <button
                // onClick={() => window.location.href = '/sessions'}
                className="flex-1 btn btn-sm btn-primary dash"
              >
                <Users className="w-4 h-4 mr-1" />
                Manage Sessions
              </button>
              </a>
              <a href="/analytics" className="flex-1">
              <button
                // onClick={() => window.location.href = '/analytics'}
                className="flex-1 btn btn-sm btn-secondary dash"
              >
                <Activity className="w-4 h-4 mr-1" />
                View Analytics
              </button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;

